/**
 * sectionStatusIcon — the shared status dot for a discovery section row.
 *
 * One definition for every surface that renders a section list: the
 * /discovery path-detail timeline, the header's path-progress popover, and the
 * topic-detail checklist. The three marker shapes are the recognisable part of
 * that timeline, so they must not drift; only their colour varies, which is
 * what lets the topic surface carry its own identity hue instead of the path
 * surface's green.
 *
 * @module components/sectionStatusIcon
 */

/**
 * Status dot for a discovery section.
 *
 * Three states, matching the API's own vocabulary: empty ring (nothing
 * started), thick ring on white (started, not resolved), solid disc with a
 * white checkmark (resolved). The white fill (rather than none) is what keeps
 * the ring legible on the popover's tinted current row.
 *
 * An unknown or absent status falls through to the not-started ring rather than
 * throwing — callers pass `entry.status` straight from the API, where the field
 * is optional.
 *
 * @param {'not_started'|'in_progress'|'complete'} status
 * @param {Object} [options]
 * @param {string} [options.color='#3d9970'] - Fill/stroke for the resolved and
 *   in-progress marks. Paths keep the green; the topic checklist passes the
 *   topic's own palette hue so the colour of the wedge the user clicked runs
 *   through the row and its section marks.
 * @param {string} [options.emptyColor='#cfd4da'] - Stroke for the not-started
 *   ring.
 * @returns {string} SVG markup
 */
export function renderSectionStatusIcon(status, { color = '#3d9970', emptyColor = '#cfd4da' } = {}) {
  if (status === 'complete') {
    // The check is inset from the disc's edge rather than spanning it, with the
    // stroke thinned to match. Retune the two together — shrinking the geometry
    // alone changes the mark's weight, not just its size.
    return `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" fill="${color}"/>
        <path d="M6.3 10.4L9.2 12.8L13.2 8" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }
  if (status === 'in_progress') {
    // r=7.75 with a 4px stroke puts the outer edge at 9.75 — the same overall
    // diameter as the 1.5px ring, so the dot column stays optically aligned
    // while the ring itself reads as heavily as the filled check.
    return `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="7.75" fill="#ffffff" stroke="${color}" stroke-width="4"/>
      </svg>
    `;
  }
  return `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="9" stroke="${emptyColor}" stroke-width="1.5"/>
    </svg>
  `;
}
